import { useCallback, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getEnv } from "@/config/env";

const EVIDENCE_BASE_API_URL =
  getEnv("VITE_EVIDENCE_BASE_API_URL");

export function useDocumentFile() {
  const { getAccessToken } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFile = useCallback(
    async (documentId: string) => {
      setIsLoading(true);
      setError(null);
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });

      try {
        const token = await getAccessToken();
        if (!token) throw new Error("Not authenticated");

        const response = await fetch(
          `${EVIDENCE_BASE_API_URL}/api/evidence/documents/${documentId}/file`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error(`Failed to load file: ${response.status}`);

        const blob = await response.blob();
        setBlobUrl(URL.createObjectURL(blob));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load file");
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken]
  );

  const clearFile = useCallback(() => {
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setError(null);
  }, []);

  return { blobUrl, isLoading, error, fetchFile, clearFile };
}
